import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import AdminSidebar from './AdminSidebar';
import '../css/AdminManagement.css';
import '../css/GroupMatchAdmin.css';
import { matchesSearch } from '../utils/textSearch';

const formatDateTime = value => value
  ? new Date(value).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })
  : '-';

function GroupMatchAdmin() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || 'null'), []);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [updatingMatchId, setUpdatingMatchId] = useState(null);
  const [reopeningMatchId, setReopeningMatchId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');

  const filteredMatches = useMemo(() => {
    const keyword = search.trim().toLowerCase().replace(/^#/, '');
    if (!keyword) return matches;
    return matches.filter(match => {
      const matchNumbers = [match.groupMatchNo, match.matchId].filter(Boolean).map(String);
      const playerNames = [...(match.team1 || []), ...(match.team2 || [])].map(player => player.name || '');
      return [...matchNumbers, ...playerNames].some(value => matchesSearch(value, keyword));
    });
  }, [matches, search]);

  const authFetch = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'ทำรายการไม่สำเร็จ');
    return data;
  }, []);

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authFetch(`/api/admin/groups/${groupId}/finished-matches`);
      setMatches(data.matches || []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, groupId]);

  useEffect(() => {
    if (!user || String(user.role).toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
    loadMatches();
  }, [loadMatches, navigate, user]);

  const deleteMatch = async match => {
    if (!window.confirm(`ลบ Match #${match.groupMatchNo || match.matchId} ใช่หรือไม่?`)) return;
    try {
      setDeleting(true);
      await authFetch(`/api/admin/groups/${groupId}/finished-matches/${match.matchId}`, { method: 'DELETE' });
      setMatches(current => current.filter(item => item.matchId !== match.matchId));
    } catch (deleteError) {
      alert(deleteError.message);
    } finally {
      setDeleting(false);
    }
  };

  const deleteAll = async () => {
    if (matches.length === 0) return;
    if (!window.confirm(`ลบ Match End ทั้งหมด ${matches.length} Match ใช่หรือไม่?\nการลบไม่สามารถย้อนกลับได้`)) return;
    try {
      setDeleting(true);
      await authFetch(`/api/admin/groups/${groupId}/finished-matches`, { method: 'DELETE' });
      setMatches([]);
    } catch (deleteError) {
      alert(deleteError.message);
    } finally {
      setDeleting(false);
    }
  };

  const updateResult = async (match, winnerTeam) => {
    const resultLabel = winnerTeam === 'TEAM1'
      ? 'Team 1 ชนะ'
      : winnerTeam === 'TEAM2' ? 'Team 2 ชนะ' : 'เสมอ';
    if (!window.confirm(`เปลี่ยนผล Match #${match.groupMatchNo || match.matchId} เป็น “${resultLabel}” ใช่หรือไม่?\nระบบจะคำนวณคะแนนผู้เล่นใหม่`)) return;

    try {
      setUpdatingMatchId(match.matchId);
      setError('');
      await authFetch(`/api/game-match/${match.matchId}/result`, {
        method: 'PATCH',
        body: JSON.stringify({ winnerTeam })
      });
      await loadMatches();
    } catch (updateError) {
      setError(updateError.message);
    } finally {
      setUpdatingMatchId(null);
    }
  };

  const reopenMatch = async match => {
    const matchNo = match.groupMatchNo || match.matchId;
    if (!window.confirm(`Re-open Match #${matchNo} กลับไป Court ${match.courtNo} ใช่หรือไม่?\nระบบจะย้อนคะแนนจากผลเดิม และเปลี่ยนผู้เล่นทั้ง 4 คนกลับเป็น PLAY`)) return;

    try {
      setReopeningMatchId(match.matchId);
      setError('');
      setNotice('');
      const data = await authFetch(`/api/admin/groups/${groupId}/finished-matches/${match.matchId}/reopen`, {
        method: 'POST'
      });
      setNotice(`✅ ${data.message}`);
      setMatches(current => current.filter(item => item.matchId !== match.matchId));
    } catch (reopenError) {
      setError(reopenError.message);
    } finally {
      setReopeningMatchId(null);
    }
  };

  const getResultLabel = result => {
    if (result === 'team1_win') return 'Team 1 ชนะ';
    if (result === 'team2_win') return 'Team 2 ชนะ';
    if (result === 'draw') return 'เสมอ';
    return 'ยังไม่ระบุผล';
  };

  if (!user || String(user.role).toLowerCase() !== 'admin') return null;

  return (
    <main className="admin-management admin-shell-with-sidebar">
      <AdminSidebar active="matches" groupId={groupId} onRefresh={loadMatches} />
      <header className="admin-management-header">
        <div>
          <button type="button" className="admin-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <h1>🏸 Group Match Admin</h1>
          <p>Match End ทั้งหมดของ Group #{groupId}</p>
        </div>
        <div className="admin-header-actions">
          <button type="button" onClick={loadMatches} disabled={loading || deleting}>🔄 รีเฟรช</button>
          <button type="button" className="danger" onClick={deleteAll} disabled={matches.length === 0 || deleting}>🗑 ลบทั้งหมด</button>
        </div>
      </header>

      {error && <div className="admin-message error">{error}</div>}
      {notice && <div className="admin-message success">{notice}</div>}
      <section className="admin-match-search">
        <label htmlFor="group-match-search">🔎 ค้นหา Match เพื่อแก้ผล</label>
        <input id="group-match-search" type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="ค้นหาเลข Match หรือชื่อผู้เล่น..." />
        <span>พบ {filteredMatches.length} จาก {matches.length} Match</span>
      </section>
      {loading ? <div className="admin-empty">กำลังโหลด...</div> : matches.length === 0 ? (
        <div className="admin-empty">ยังไม่มี Match End</div>
      ) : filteredMatches.length === 0 ? (
        <div className="admin-empty">ไม่พบเลข Match หรือชื่อผู้เล่นที่ค้นหา</div>
      ) : (
        <section className="admin-match-list">
          {filteredMatches.map(match => (
            <article className="admin-match-card" key={match.matchId}>
              <div className="admin-match-title">
                <div>
                  <strong>Match #{match.groupMatchNo || match.matchId}</strong>
                  {Number(match.reopenCount || 0) > 0 && (
                    <span className="admin-reopen-badge" title={`Re-open ล่าสุด ${formatDateTime(match.reopenedAt)}`}>
                      ↩ RE-OPEN {Number(match.reopenCount) > 1 ? `×${match.reopenCount}` : ''}
                    </span>
                  )}
                  <span>Court {match.courtNo || '-'} • {formatDateTime(match.endTime || match.createdAt)}</span>
                </div>
                <div className="admin-match-card-actions">
                  <button type="button" className="reopen" disabled={deleting || reopeningMatchId !== null || updatingMatchId !== null} onClick={() => reopenMatch(match)}>
                    {reopeningMatchId === match.matchId ? 'กำลัง Re-open…' : `↩ Re-open ที่ Court ${match.courtNo || '-'}`}
                  </button>
                  <button type="button" className="danger" disabled={deleting || reopeningMatchId !== null} onClick={() => deleteMatch(match)}>ลบ Match</button>
                </div>
              </div>
              <div className="admin-teams">
                <div><b>Team 1</b>{match.team1.map(player => <span key={player.userId}>{player.name} <small>{player.rank} ({player.pointChange >= 0 ? '+' : ''}{player.pointChange})</small></span>)}</div>
                <strong>VS</strong>
                <div><b>Team 2</b>{match.team2.map(player => <span key={player.userId}>{player.name} <small>{player.rank} ({player.pointChange >= 0 ? '+' : ''}{player.pointChange})</small></span>)}</div>
              </div>
              <div className="admin-result-editor">
                <div className="admin-result">ผลการแข่งขัน: {getResultLabel(match.result)}</div>
                <div className="admin-result-actions" aria-label={`แก้ผล Match ${match.groupMatchNo || match.matchId}`}>
                  <button type="button" className={`team1${match.result === 'team1_win' ? ' is-selected' : ''}`} disabled={updatingMatchId !== null || deleting} onClick={() => updateResult(match, 'TEAM1')}>T1 Win</button>
                  <button type="button" className={`draw${match.result === 'draw' ? ' is-selected' : ''}`} disabled={updatingMatchId !== null || deleting} onClick={() => updateResult(match, 'DRAW')}>Draw</button>
                  <button type="button" className={`team2${match.result === 'team2_win' ? ' is-selected' : ''}`} disabled={updatingMatchId !== null || deleting} onClick={() => updateResult(match, 'TEAM2')}>T2 Win</button>
                  {updatingMatchId === match.matchId && <span>กำลังคำนวณคะแนนใหม่...</span>}
                </div>
              </div>
            </article>
          ))}
        </section>
      )}
    </main>
  );
}

export default GroupMatchAdmin;
